import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, bookingApi, unwrap, type ApiEnvelope } from '@/lib/api/client';
import type {
  BookingLocation,
  MembershipBenefit,
  MembershipPlan,
  ServiceType,
} from './types';

/**
 * Membership plans and their benefits, from the booking service.
 *
 * The booking API returns its payload directly under `data` — no `results`
 * envelope, unlike the order endpoints — so `unwrap` lands on the array.
 */

const get = <T>(url: string) => unwrap<T>(bookingApi.get<ApiEnvelope<T>>(url));

export function useMembershipPlans() {
  return useQuery({
    queryKey: ['memberships', 'plans'],
    queryFn: () => get<MembershipPlan[]>('subscriptions'),
    staleTime: 60_000,
  });
}

/** Locations the plan can be scoped to; `null` means every location. */
export function useBookingLocations(enabled = true) {
  return useQuery({
    queryKey: ['booking', 'locations'],
    queryFn: () => get<BookingLocation[]>('locations'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useServiceTypes(enabled = true) {
  return useQuery({
    queryKey: ['booking', 'service-types'],
    queryFn: () => get<ServiceType[]>('servicetypes/list'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useMembershipBenefits(planId: string | null) {
  return useQuery({
    queryKey: ['memberships', planId, 'benefits'],
    queryFn: () =>
      get<MembershipBenefit[]>(`subscriptions/${planId}/benefits/all`),
    enabled: Boolean(planId),
  });
}

function useInvalidatePlans() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['memberships'] });
}

/** `PUT` when the plan already exists, `POST` when it does not. */
export function useSaveMembershipPlan() {
  const invalidate = useInvalidatePlans();

  return useMutation({
    mutationFn: (plan: Partial<MembershipPlan> & { id?: string }) =>
      plan.id
        ? unwrap<unknown>(bookingApi.put('subscriptions', plan))
        : unwrap<unknown>(bookingApi.post('subscriptions', plan)),
    onSuccess: invalidate,
  });
}

export function useDeleteMembershipPlan() {
  const invalidate = useInvalidatePlans();

  return useMutation({
    mutationFn: (planId: string) =>
      unwrap<unknown>(bookingApi.delete(`subscriptions/${planId}`)),
    onSuccess: invalidate,
  });
}

export function useSaveBenefit() {
  const invalidate = useInvalidatePlans();

  return useMutation({
    mutationFn: (benefit: Partial<MembershipBenefit> & { id?: string }) =>
      benefit.id
        ? unwrap<unknown>(bookingApi.put('subscriptions/benefit', benefit))
        : unwrap<unknown>(bookingApi.post('subscriptions/benefit', benefit)),
    onSuccess: invalidate,
  });
}

export function useDeleteBenefit() {
  const invalidate = useInvalidatePlans();

  return useMutation({
    mutationFn: (benefitId: string) =>
      unwrap<unknown>(bookingApi.delete(`subscriptions/benefit/${benefitId}`)),
    onSuccess: invalidate,
  });
}

/**
 * Image upload.
 *
 * Goes to the *main* API, not the booking one, and to a different path for
 * plans and for benefits — both quirks are the original's.
 */
export function useUploadImage(kind: 'subscriptions' | 'benefits') {
  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('file', file);
      return unwrap<string>(
        api.post(`utility/${kind}/uploadfile`, body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      );
    },
  });
}
